/** Formatting in the company's own timezone and currency (set once `/me` loads). */
// Until the company loads: the browser's own time zone and locale (never one company's or country's).
let TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' } })()
let CURRENCY = 'USD'
let LOCALE: string | undefined = undefined

export function setCompanyLocale(c: { timezone?: string; currency?: string; region?: string; locale?: string }) {
  if (c.timezone) TZ = c.timezone
  if (c.currency) CURRENCY = c.currency
  // The company's own locale when given, else the usual one for its data region.
  LOCALE = c.locale || (({ au: 'en-AU', uk: 'en-GB', eu: 'en-IE', na: 'en-US' } as Record<string, string>)[c.region ?? ''] ?? LOCALE)
}
/** The company's locale for number and date formatting (undefined: the browser's). */
export const currentLocale = () => LOCALE
export const companyTz = () => TZ
export const companyCurrency = () => CURRENCY

export function money(minor: number | null | undefined, currency = CURRENCY, opts: { cents?: boolean } = {}) {
  if (minor === null || minor === undefined) return '–'
  const v = minor / 100
  const cents = opts.cents ?? !Number.isInteger(v)
  return new Intl.NumberFormat(LOCALE, { style: 'currency', currency, minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }).format(v)
}

export function num(n: number | null | undefined, digits = 0) {
  if (n === null || n === undefined) return '–'
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n)
}

export function pct(r: number | null | undefined, digits = 0) {
  if (r === null || r === undefined) return '–'
  return `${(r * 100).toFixed(digits)}%`
}

function d(x: string | number | Date) { return x instanceof Date ? x : new Date(x) }

/** "Wed 10:31" for this week, "3 Oct 10:31" otherwise. */
export function when(x: string | null | undefined) {
  if (!x) return '–'
  const date = d(x)
  const days = Math.abs(Date.now() - date.getTime()) / 86_400_000
  const time = new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
  if (days < 0.5 && sameDay(date, new Date())) return `Today ${time}`
  if (days < 6) return `${new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, weekday: 'short' }).format(date)} ${time}`
  return `${dateOnly(date)} ${time}`
}

function sameDay(a: Date, b: Date) {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
  return f.format(a) === f.format(b)
}

export function dateOnly(x: string | Date | null | undefined, withYear = false) {
  if (!x) return '–'
  const date = d(x)
  const thisYear = new Date().getFullYear() === date.getFullYear()
  return new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, day: 'numeric', month: 'short', ...(withYear || !thisYear ? { year: 'numeric' } : {}) }).format(date)
}

export function longToday() {
  return new Intl.DateTimeFormat(LOCALE, { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date()).replace(/ at /, ' · ').replace(/, (\d\d:\d\d)$/, ' · $1')
}

export function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: '2-digit', hourCycle: 'h23' }).format(new Date()))
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

/** "9m", "3h", "2d" from now (absolute). */
export function ago(x: string | null | undefined, now = Date.now()) {
  if (!x) return '–'
  const s = Math.round((now - d(x).getTime()) / 1000)
  return span(Math.abs(s)) + (s >= 0 ? ' ago' : '')
}

export function span(sec: number) {
  if (sec < 60) return `${Math.max(0, Math.round(sec))}s`
  const m = Math.round(sec / 60)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.round(h / 24)}d`
}

/** Deadline text and colour: "due 47m" amber, "overdue 5m" red. */
export function due(deadline: string | null | undefined, now = Date.now()): { text: string; tone: 'red' | 'amber' | 'muted' } {
  if (!deadline) return { text: 'no deadline', tone: 'muted' }
  const s = (d(deadline).getTime() - now) / 1000
  if (s < 0) return { text: `overdue ${span(-s)}`, tone: 'red' }
  if (s < 15 * 60) return { text: `due ${span(s)}`, tone: 'red' }
  if (s < 90 * 60) return { text: `due ${span(s)}`, tone: 'amber' }
  return { text: `due ${span(s)}`, tone: 'muted' }
}

export function duration(sec: number | null | undefined) {
  if (sec === null || sec === undefined) return '–'
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

export function clock(fromIso: string, now = Date.now()) {
  const s = Math.max(0, Math.floor((now - d(fromIso).getTime()) / 1000))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

export function initials(name: string | null | undefined) {
  if (!name) return '?'
  const parts = name.replace(/[^\p{L}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '#'
  return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

/** snake_case or kebab to "Sentence case". */
export function humanize(s: string | null | undefined) {
  if (!s) return ''
  const t = s.replace(/[_-]+/g, ' ').trim()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

export function plural(n: number, one: string, many = one + 's') { return `${num(n)} ${n === 1 ? one : many}` }

/** Renders a CRM field value in plain words. */
export function fieldValue(v: unknown, type?: string): string {
  if (v === null || v === undefined || v === '') return '–'
  if (type === 'money' && typeof v === 'number') return money(v)
  if (type === 'boolean' || typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (type === 'date' && typeof v === 'string') return dateOnly(v + 'T00:00:00', true)
  if (type === 'datetime' && typeof v === 'string') return when(v)
  if (Array.isArray(v)) return v.join(', ')
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
